import { type ReactNode } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { Stepper, type StepDef } from '@/components/ui/Stepper';
import { cn } from '@/lib/cn';

/**
 * Sprint-2 DREAM-18 wizard chrome.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Stepper (horizontal on md+, vertical rail on mobile)   │
 *   ├──────────────────────────────────────┬──────────────────┤
 *   │  Body (the active step's content)    │  Audit Trail     │
 *   │                                      │  (right rail)    │
 *   │                                      │                  │
 *   ├──────────────────────────────────────┴──────────────────┤
 *   │  Back  |  Save & Resume Later                Next →     │
 *   └─────────────────────────────────────────────────────────┘
 *
 * The parent owns step state + form draft state; this component is purely
 * presentational. `auditPanel` is a slot — the consumer renders the
 * AuditTrailPanel component with the right scope.
 */

export interface WizardShellProps {
  steps: StepDef[];
  activeKey: string;
  onStepClick?: (key: string) => void;
  children: ReactNode;
  auditPanel?: ReactNode;
  // Footer
  onBack?: () => void;
  onNext?: () => void;
  onSaveAndExit?: () => void;
  isSaving?: boolean;
  isAdvancing?: boolean;
  nextLabel?: string;
  /** Disable Next when the current step's form isn't valid. */
  nextDisabled?: boolean;
}

export function WizardShell({
  steps,
  activeKey,
  onStepClick,
  children,
  auditPanel,
  onBack,
  onNext,
  onSaveAndExit,
  isSaving,
  isAdvancing,
  nextLabel = 'Next',
  nextDisabled,
}: WizardShellProps) {
  const isFirstStep = steps.findIndex((s) => s.key === activeKey) === 0;
  return (
    <div className="card flex flex-col">
      <div className="border-b border-surface-border bg-surface-muted/60 px-4 py-3">
        <Stepper steps={steps} onStepClick={onStepClick} />
      </div>
      {/* Left content drives the container height; aside is taken out of flow
          so it can never push the row taller than the left section. */}
      <div className="relative">
        <div className={cn('min-w-0 p-5', auditPanel ? 'lg:mr-[320px]' : '')}>
          {children}
        </div>
        {auditPanel && (
          <aside className="hidden lg:flex lg:flex-col lg:absolute lg:top-0 lg:right-0 lg:bottom-0 lg:w-[320px] border-l border-surface-border bg-surface-muted/30 overflow-hidden">
            {auditPanel}
          </aside>
        )}
      </div>
      <footer className="flex items-center justify-between gap-2 border-t border-surface-border bg-surface-muted/60 px-4 py-3">
        <button
          type="button"
          className="btn-ghost text-xs disabled:opacity-50"
          onClick={onBack}
          disabled={!onBack || isFirstStep || isAdvancing || isSaving}
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          {onSaveAndExit && (
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={onSaveAndExit}
              disabled={isSaving || isAdvancing}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save & Resume Later
            </button>
          )}
          {onNext && (
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={onNext}
              disabled={isAdvancing || isSaving || nextDisabled}
            >
              {isAdvancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
              {nextLabel}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
